/**
 * Created by User on 26/9/2559.
 */
import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {Observable, Subject, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {Toast} from './shared/toast/toast';
import {Router} from '@angular/router';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import {Path} from './shared/path';
import {TranslateService} from '@ngx-translate/core';
import { OneDriveItem } from './dms/onedrive-list/onedrive-item.type';
import { OneDriveListDriveItemAPIResponse } from './dms/onedrive/onedrive.type';
import { deleteCookie } from './shared/cookie';
import { AnnotationAttributes, zDoxItem } from './shared/types';
import { MetadataFormProperty } from './dms/viewer-controller/viewer-info-panel/viewer-info-panel.types';
import { DeviceDetectorService } from 'ngx-device-detector';
import jwt_decode from "jwt-decode";
import { TaskAttribute, TaskAttribute_Response } from './e-signature/create-task/create-task.type';
import { environment } from 'environments/environment';

declare const serviceUrl: string;
declare const google_id: string;
declare var google: any;

export class Error {
   readonly status: number;
   readonly message: string;
   readonly redirectUrl?: string;

   constructor(status: number, message: string, redirectUrl?: string) {
      this.status = status;
      this.message = message;
      this.redirectUrl = redirectUrl;
   }

   toString(): string {
      return this.message;
   }
}

@Injectable()
export class IdexService {
   public readonly url = serviceUrl;
   public user: any;
   public role = 'Admin';
   public shareType: 'Public' | 'Anyone' | 'Private' = 'Private';
   public shareId: string;
   public shareDocId: string;
   public selectedDocId: string;
   public isDownload = true;
   public permissionLevel = 0;
   public logoUrl: string;
   public browserInfo: string;
   public platform: string;
   public location: string;
   // Emits the site TIN whenever the current company/site context changes, so components
   // (e.g. the admin menu) can react once the site is actually known.
   public readonly siteChange = new Subject<string>();
   public readonly SCOPE = 'https://www.googleapis.com/auth/drive ' +
      'https://www.googleapis.com/auth/drive.appdata ' +
      'https://www.googleapis.com/auth/drive.file ' +
      'https://www.googleapis.com/auth/drive.metadata ' +
      'https://www.googleapis.com/auth/drive.metadata.readonly ' +
      'https://www.googleapis.com/auth/drive.photos.readonly ' +
      'https://www.googleapis.com/auth/drive.readonly';
   DISCOVERY_DOCS = ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'];

   constructor(private http: HttpClient, private toast: Toast, private router: Router, private dialog: MatDialog,
               private translate: TranslateService, private deviceService: DeviceDetectorService) {
      try {
         this.user = JSON.parse(localStorage.getItem('idex-user'));
         if ('userAgentData' in navigator) {
            (navigator as any).userAgentData.getHighEntropyValues(["platformVersion"])
               .then(ua => {
                  const platform = (navigator as any).userAgentData.platform;
                  const majorVersion = parseInt(ua.platformVersion.split('.')[0]);
                  if (platform === "Windows") {
                     if (majorVersion >= 13) {
                        this.platform = "windows-11";
                     } else if (majorVersion > 0) {
                        this.platform = "windows-10";
                     }
                  }
               });
         }
         if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(position => {
               const lat = position.coords.latitude;
               const lng = position.coords.longitude;
               const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`;
               this.http.get<any>(url).subscribe(res => {
                  const address = res.address;
                  this.location = address.province + ', ' + address.country;
               }, error => {
                  console.error('not found address: ',error);
                  this.location = error.message;
               });
            }, error => {
               console.error('Geolocation error:', error);
               this.location = error.message;
            });
         } else {
            console.log('Geolocation not supported');
            this.location = 'Geolocation not supported';
         }
      } catch (e) {
         console.error('Error initializing user from localStorage:', e);
      }
   }

   private createHttpOptions(options?: any, json?: boolean): any {
      if (options && options.headers) {
         return options;
      }

      let headers = new HttpHeaders({
         'Accept-Language': localStorage.getItem('lang') || 'en',
         'Cache-Control': 'no-cache',
         'Pragma': 'no-cache',
         'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT'
      });
      if (json !== false) {
         headers = headers.append('Content-Type', 'application/json;charset=utf-8');
      }
      if (this.user?.token) {
         headers = headers.append('X-Auth-Token', this.user.token);
      }
      if (options) {
         options.headers = headers;
      } else {
         options = { headers };
      }
      return options;
   }

   private getUrl(api: string): string {
      if (/^https?:\/\//.test(api)) {
         return api;
      }
      return this.url + (api.startsWith('/') ? api : '/' + api);
   }

   get(api: string, options?: any, body?: any): Observable<any> {
      let json = false;
      if (body?.isBody === true && typeof body === 'object' && !(body instanceof FormData)) {
         body = JSON.stringify(body.data);
         json = true;
      }
      return this.http.get(this.getUrl(api), this.createHttpOptions(options, json))
         .pipe(catchError(this.handleError(api)));
   }

   post(api: string, body?: any, options?: any): Observable<any> {
      let json = false;
      if (typeof body === 'object' && !(body instanceof FormData)) {
         body = JSON.stringify(body);
         json = true;
      }
      return this.http.post(this.getUrl(api), body, this.createHttpOptions(options, json))
         .pipe(catchError(this.handleError(api)));
   }

   put(api: string, body?: any, options?: any): Observable<any> {
      let json = false;
      if (typeof body === 'object' && !(body instanceof FormData)) {
         body = JSON.stringify(body);
         json = true;
      }
      return this.http.put(this.getUrl(api), body, this.createHttpOptions(options, json))
         .pipe(catchError(this.handleError(api)));
   }

   delete(api: string, options?: any): Observable<any> {
      return this.http.delete(this.getUrl(api), this.createHttpOptions(options))
         .pipe(catchError(this.handleError(api)));
   }

   private handleError(api: string) {
      return (error: any): Observable<any> => {
         let alert = true;
         let status = 0;
         let message: string;
         const url = window.location.pathname.replace('/idex', '');
         if (error instanceof HttpErrorResponse) {
            status = error.status;
            if (error.status === 401 && !error.url.includes('googledrive') && !api.includes('/notification/')
               && !api.includes('sso/login')) {
               this.dialog.closeAll();
               this.setUser();
               alert = api === '/login';
               const path = location.pathname;
               if (!url.startsWith('/login')) {
                  let shareId;
                  if (url.startsWith('/share/') || url.startsWith('/share-public/')) {
                     shareId = url.replace('/share-public/', '')
                        .replace('/share/', '').split('/')[0];
                     const share_url = Path.combine('/login', encodeURIComponent('share/'), encodeURIComponent(shareId));
                     this.router.navigate([share_url]);
                  } else {
                     this.translate.get('timeout').subscribe(s => {
                        this.toast.error(s);
                     });
                     const pathUrl = Path.combine('/login', path);
                     this.router.navigate([pathUrl]);
                  }
               }
            }

            if ((error.status === 403 || error.status === 200 ) && api.includes('/notification/')) {
               this.dialog.closeAll();
               this.setUser();
               alert = false;
               if (!url.startsWith('/login')) {
                  this.translate.get('timeout').subscribe(s => {
                     this.toast.error(s);
                  });
                  this.router.navigate(['/login']);
               }
            }

            if (error.status === 591) {
               this.dialog.closeAll();
               return throwError(error);
            }

            if (error.error && typeof error.error === 'string') {
               message = error.error;
            } else {
               if (error.error) {
                  message = error.error.message;
               }
               if (!message) {
                  message = error.message;
               }
            }
            if (error.status === 0) {
               message = 'Connection lost: The server closed the connection';
            }
         } else {
            console.error(error);
            message = error.message || error;
         }
         // IdP-only response: the account must sign in through an Identity Provider. Surface the
         // redirect target to the caller and let the page show it inline instead of a toast.
         const redirectUrl = error?.error?.redirectUrl;
         if (redirectUrl) {
            alert = false;
         }
         // OIDC/SSO login failures (e.g. an account that exists in neither the users table nor
         // Alfresco is rejected) are surfaced inline on the /oauth page by oauth.component, with a
         // back-to-login link. Suppress the global toast so it isn't shown twice; the 401 branch
         // above is already skipped for this endpoint so the error reaches the component instead of
         // triggering a session-timeout redirect.
         if (api.includes('sso/login')) {
            alert = false;
         }
         // Duplicate allowed-IP: the Add/Edit IP dialog already validates and shows this inline
         // under the field, so suppress the global toast and let the dialog surface it alone.
         if (api.includes('/allowed-ip') && message && message.includes('already exists')) {
            alert = false;
         }
         // IdP-managed account on Forgot Password: the dialog already shows the backend message
         // inline under the field, so suppress the global toast and let it surface there alone.
         if (api.includes('/forgotpw') && message && message.includes('IdP')) {
            alert = false;
         }
         if (alert) {
            if (url.startsWith('/file/')) {
               this.translate.get('notification.file_not_exist').subscribe(s => {
                  message = s;
                  this.toast.error(s);
               })
            } else if (!(url.startsWith('/share/') && message.includes('Node does not exist')) && !message.includes('".tag"') &&
               !(api.startsWith('/trash/') && message.includes('Access Denied.'))) {
               this.toast.error(message);
            }
         }
         return throwError(new Error(status, message, redirectUrl));
      };
   }

   setUser(user?: any): void {
      this.user = user;
      if (user) {
         localStorage.setItem('idex-user', JSON.stringify(user));
      } else {
         localStorage.removeItem('idex-user');
      }
   }

   // Whether the current session signed in through an Identity Provider (SSO/OIDC).
   // Set only on the oauth/SSO login path; cleared on normal login and logout. Used to hide
   // password self-service (e.g. Change Password) for IdP-managed accounts.
   setIdpLogin(value: boolean): void {
      if (value) {
         localStorage.setItem('idp-login', 'true');
      } else {
         localStorage.removeItem('idp-login');
      }
   }

   get isIdpLogin(): boolean {
      return localStorage.getItem('idp-login') === 'true';
   }

   login(email: string, password: string): Observable<any> {
      this.user = undefined;
      const options = this.createHttpOptions();
      const auth = btoa(email.toLowerCase() + ':' + encodeURIComponent(password));
      options.headers = options.headers.append('Authorization', 'Basic ' + auth);
      this.browserInfo = this.getBrowserInfo();
      const queryParams = `?device=Web&browserInfo=${this.browserInfo}&location=${this.location}`;
      return this.post('/login' + queryParams, null, options);
   }

   loginWith(data: any): Observable<any> {
      this.browserInfo = this.getBrowserInfo();
      const queryParams = `?device=Web&browserInfo=${this.browserInfo}&location=${this.location}`;
      return this.post('/signin' + queryParams, data);
   }

   twoFactorLogin(code: string, key: string): Observable<any> {
      const options = this.createHttpOptions();
      return this.post('/twofactor/login', {code: code, key: key}, options);
   }

   twoFactorVerification(code: string): Observable<any> {
      const options = this.createHttpOptions();
      return this.post('/twofactor/verification', {code: code}, options);
   }

   twoFactorVerificationPassword(password: string): Observable<any> {
      const options = this.createHttpOptions();
      const pass = btoa(encodeURIComponent(password));
      return this.post('/twofactor/verificationPassword', {password: pass}, options);
   }

   getTwoFactorQr() {
      return this.get('/twofactor/qrcode');
   }

   getOTP(key: string) : Observable<any> {
      const api = '/twofactor/otp?key=' + key;
      return this.get(api);
   }

   getExternalUser(key: string) : Observable<any> {
      const api = '/twofactor/ext-user?key=' + key;
      return this.get(api);
   }

   verificationOTP(key: string, otp) : Observable<any> {
      const options = this.createHttpOptions();
      return this.post('/twofactor/verification-otp', {key: key,otp: otp }, options);
   }
   sendTwoFactorQr(key: string): Observable<any> {
      const options = this.createHttpOptions();
      return this.post('/twofactor/send-qrcode', {key: key}, {responseType: 'text'});
   }

   logout() {
      this.setIdpLogin(false);
      this.delete('/auth?device=Web').subscribe().add(() => { this.setUser(); });
   }

   logoutGoogleApi() {
      if (sessionStorage.getItem('token_google')){
         const tokenGoogle: any = jwt_decode(sessionStorage.getItem('token_google') as string);

         google.accounts.id.revoke(tokenGoogle.email, done => {
            // This is intentional.
         });
         sessionStorage.removeItem('token_google');
         sessionStorage.removeItem('access_token_google');
      }

      if (sessionStorage.getItem('access_token_google')) {
         sessionStorage.removeItem('access_token_google');
      }
   }

   removeMicrosoftAuth() {
      deleteCookie("odauth");
      localStorage.removeItem('access_token');
      localStorage.removeItem('onedrive_callback');
      localStorage.removeItem('onedrive_action');
   }

   removeDropboxAuth() {
      localStorage.removeItem('access_token_dropbox');
      localStorage.removeItem('dropbox_auth');
      localStorage.removeItem('refresh_token_dropbox');
   }

   register(email: string, password: string, firstname: string, lastname: string, token: string) {
      this.browserInfo = this.getBrowserInfo();
      const body = {email: email, password: password, firstname: firstname, lastname: lastname, token: token, browserInfo: this.browserInfo, location: this.location};
      return this.post('/signup?device=Web', body, {
         headers: new HttpHeaders({
            'Accept-Language': localStorage.getItem('lang') || 'en',
            'Content-Type': 'application/json;charset=utf-8'
         }),
         responseType: 'text'
      });
   }

   registerwithetax(data: any, files: File[]) {
      const body_data = {
         tin: data.tin,
         name: data.name,
         username: data.username,
         password: data.taxpassword,
         server: data.server,
         port: data.port,
         cellphone: data.cellphone
      };
      const body_profile = {
         email: data.email,
         password: data.password,
         firstname: data.firstname,
         lastname: data.lastname
      };
      const formData = new FormData();
      formData.append('profile', new Blob([JSON.stringify(body_profile)], {type: 'application/json'}));
      formData.append('data', new Blob([JSON.stringify(body_data)], {type: 'application/json'}));
      for (let i = 0; i < files.length; i++) {
         formData.append('file', files[i]);
      }
      return this.post('/signup/etax?device=Web', formData, {
         headers: new HttpHeaders({
            'Accept-Language': localStorage.getItem('lang') || 'en'
         })
      });
   }

   registerwithcompany(data: any) {
      this.browserInfo = this.getBrowserInfo();
      const body_data = {
         tin: data.tin,
         name: data.name,
         cellphone: data.cellphone
      };
      const body_profile = {
         email: data.email,
         password: data.password,
         firstname: data.firstname,
         lastname: data.lastname,
         token: data.token,
         browserInfo: this.browserInfo,
         location: this.location
      };
      const formData = new FormData();
      formData.append('profile', new Blob([JSON.stringify(body_profile)], {type: 'application/json'}));
      formData.append('data', new Blob([JSON.stringify(body_data)], {type: 'application/json'}));
      return this.post('/signup/create/company?device=Web', formData, {
         headers: new HttpHeaders({
            'Accept-Language': localStorage.getItem('lang') || 'en'
         }),
         responseType: 'text'
      });
   }

   registerCompanyWithetax(data: any, files: File[]) {
      this.browserInfo = this.getBrowserInfo();
      const body_data = {
         tin: data.tin,
         name: data.name,
         username: data.username,
         password: data.taxpassword,
         server: data.server,
         port: data.port,
         cellphone: data.cellphone,
         billingCode: data.billingCode,
         sellerCode: data.sellerCode
      };
      const body_profile = {
         email: data.email,
         password: data.password,
         firstname: data.firstname,
         lastname: data.lastname,
         username: data.username,
         browserInfo: this.browserInfo,
         location: this.location
      };
      const body_config = {
         siteCode: data.packageConfig.siteCode,

         siteName: data.packageConfig.siteName,
         siteType: data.packageConfig.siteType,
         siteValue: data.packageConfig.siteValue,

         totalUserName: data.packageConfig.totalUserName,
         totalUserType: data.packageConfig.totalUserType,
         totalUserValue: data.packageConfig.totalUserValue,

         pricePerUserName: data.packageConfig.pricePerUserName,
         pricePerUserType: data.packageConfig.pricePerUserType,
         pricePerUserValue: data.packageConfig.pricePerUserValue,

         storagePerUserName: data.packageConfig.storagePerUserName,
         storagePerUserType: data.packageConfig.storagePerUserType,
         storagePerUserValue: data.packageConfig.storagePerUserValue,

         storagePerSiteName: data.packageConfig.storagePerSiteName,
         storagePerSiteType: data.packageConfig.storagePerSiteType,
         storagePerSiteValue: data.packageConfig.storagePerSiteValue,

         apiUsageName: data.packageConfig.apiUsageName,
         apiUsageType: data.packageConfig.apiUsageType,
         apiUsageValue: data.packageConfig.apiUsageValue,

         signDocName: data.packageConfig.signDocName,
         signDocType: data.packageConfig.signDocType,
         signDocValue: data.packageConfig.signDocValue,

         noOfCaName: data.packageConfig.noOfCaName,
         noOfCaType: data.packageConfig.noOfCaType,
         noOfCaValue: data.packageConfig.noOfCaValue,
      }
      const formData = new FormData();
      formData.append('profile', new Blob([JSON.stringify(body_profile)], {type: 'application/json'}));
      formData.append('data', new Blob([JSON.stringify(body_data)], {type: 'application/json'}));
      formData.append('packageConfig', new Blob([JSON.stringify(body_config)], {type: 'application/json'}));
      for (let i = 0; i < files.length; i++) {
         formData.append('file', files[i]);
      }
      return this.post('/signup/company/true?device=Web', formData, {
         headers: new HttpHeaders({
            'Accept-Language': localStorage.getItem('lang') || 'en'
         })
      });
   }

   registerCompany(data: any) {
      this.browserInfo = this.getBrowserInfo();
      const body_data = {
         tin: data.tin,
         name: data.name,
         cellphone: data.cellphone,
         isCreateByAdmin: data.isCreateByAdmin,
         billingCode: data.billingCode,
         sellerCode: data.sellerCode
      };
      const body_profile = {
         email: data.email,
         firstname: data.firstname,
         lastname: data.lastname,
         browserInfo: this.browserInfo,
         location: this.location
      };
      const body_config = {
         siteCode: data.packageConfig.siteCode,

         siteName: data.packageConfig.siteName,
         siteType: data.packageConfig.siteType,
         siteValue: data.packageConfig.siteValue,

         totalUserName: data.packageConfig.totalUserName,
         totalUserType: data.packageConfig.totalUserType,
         totalUserValue: data.packageConfig.totalUserValue,

         pricePerUserName: data.packageConfig.pricePerUserName,
         pricePerUserType: data.packageConfig.pricePerUserType,
         pricePerUserValue: data.packageConfig.pricePerUserValue,

         storagePerUserName: data.packageConfig.storagePerUserName,
         storagePerUserType: data.packageConfig.storagePerUserType,
         storagePerUserValue: data.packageConfig.storagePerUserValue,

         storagePerSiteName: data.packageConfig.storagePerSiteName,
         storagePerSiteType: data.packageConfig.storagePerSiteType,
         storagePerSiteValue: data.packageConfig.storagePerSiteValue,

         apiUsageName: data.packageConfig.apiUsageName,
         apiUsageType: data.packageConfig.apiUsageType,
         apiUsageValue: data.packageConfig.apiUsageValue,

         signDocName: data.packageConfig.signDocName,
         signDocType: data.packageConfig.signDocType,
         signDocValue: data.packageConfig.signDocValue,

         noOfCaName: data.packageConfig.noOfCaName,
         noOfCaType: data.packageConfig.noOfCaType,
         noOfCaValue: data.packageConfig.noOfCaValue,
      }
      const formData = new FormData();
      formData.append('data', new Blob([JSON.stringify(body_data)], {type: 'application/json'}));
      formData.append('profile', new Blob([JSON.stringify(body_profile)], {type: 'application/json'}));
      formData.append('packageConfig', new Blob([JSON.stringify(body_config)], {type: 'application/json'}));
      return this.post('/signup/company/false?device=Web', formData, {
         headers: new HttpHeaders({
            'Accept-Language': localStorage.getItem('lang') || 'en'
         })
      });
   }

   updateCompany(companyName: string, cellPhone: string, tin: string, domain: string) {
      const body_company = {
         name: companyName,
         cellphone: cellPhone,
         tin: tin,
         domain: domain
      };
      return this.put('company/update/info?device=Web', body_company, {responseType: 'text'});
   }

   listDocuments(path: string, page: number, size: number) {
      if (path.length > 0 && path[0] !== '/') {
         path = '/' + path;
      }
      // const api = '/doclist' + path.replace(/\./g, '*') + '?page=' + page + '&size=' + size;
      // const api = '/doclist?path=' + encodeURIComponent(path).replace(/\./g, '*') + '&page=' + page + '&size=' + size;
      const api = '/doclist?path=' + encodeURIComponent(path) + '&page=' + page + '&size=' + size;
      return this.get(api);
   }

   listShareDocuments(path: string, page: number, size: number, email: string,
                      id: string, shareType: string, download: boolean, permissionLevel: number) {

      if (!parseInt(permissionLevel.toString(), 10)) {
         permissionLevel = 0;
      }
      // const api = '/sharelist' + path.replace(/\./g, '*') + '?page=' + page + '&size=' + size + '&id=' + id;
      // const api = '/sharelist' + path.replace(/\./g, '*') + '?email=' + email + '&id=' + id;
      const api = '/get/sharelist?id=' + id + '&shareType=' + shareType +
         '&download=' + download + '&level=' + permissionLevel;
      return this.post(api, path);
   }

   listDocuments_business(path: string, page: number, size: number, tin: string) {
      if (path.length > 0 && path[0] !== '/') {
         path = '/' + path;
      }
      // const api = '/work/' + tin + '/doclist' + path.replace(/\./g, '*') + '?page=' + page + '&size=' + size;
      // const api = '/work/' + tin + '/doclist?path=' + encodeURIComponent(path).replace(/\./g, '*') + '&page=' + page + '&size=' + size;
      const api = '/work/' + tin + '/doclist?path=' + encodeURIComponent(path) + '&page=' + page + '&size=' + size + '&customerId=' + environment.zdoxCustomerId;
      return this.get(api);
   }

   listShareWithMeDocuments(page: number, size: number) {
      const api = '/sharewithme?page=' + page + '&size=' + size;
      return this.get(api);
   }

   listShareByMeDocuments(page: number, size: number) {
      const api = '/sharewithother?page=' + page + '&size=' + size;
      return this.get(api);
   }

   getCompanyInfo(companyName: string, companyCode?: string) {
      let url = '/company/' + companyName + '/info';
      if (companyCode) {
         url += "?tin=" + companyCode;
      }
      return this.get(url);
   }

   updateAvatar(file: File) {
      const formData = new FormData();
      formData.append('file', file);
      return this.post('/avatar?device=Web', formData);
   }

   updateLogo(file: File,id: string,tin: string,alignment: string) {
      const formData = new FormData();
      formData.append('file', file);
      return this.post('/logo?id=' + id + '&tin=' + tin + '&alignment=' + alignment, formData);
   }

   updateLogoAlignment(data: any){
      return this.put('/logo',data);

   }

   updateDescription(description: any, id: any) {
      return this.put('/node/' + id, {description: description});
   }

   deleteFiles(id: any) {
      return this.delete('/node/' + id + '?customerId=' + environment.zdoxCustomerId);
   }

   getLogo(id: string,tin: string) {
      return this.get('/logo?id=' + id + '&tin=' + tin);
   }

   getTypes() {
      return this.get('/types');
   }

   getShareTypes() {
      return this.get('/share-public/types');
   }

   getProperties(content: any) {
      return this.get('/type/' + content);
   }

   editProperties(doc_id: any, prop_list: any) {
      return this.put('/metadata/' + doc_id, {'properties': prop_list});
   }

   sendProperties(doc_id: any, content: any, prop_list: any) {
      return this.post('/metadata', {'docId': doc_id, 'type': content, 'properties': prop_list});
   }

   getDocumentEform(id: string): Observable<zDoxItem> {
      return this.get('/eform/info/' + id + '?c=' + true);
   }

   getDocument(id: string): Observable<zDoxItem> {
      return this.get('/info/' + id + '?c=' + true);
   }

   getShareDocument(id: string, sharer: string): any {
      return this.get('/share-public/info/' + id + '?c=' + true + '&s=' + sharer);
   }

   getDocumentAdmin(id: string, tId: number, type: string): any {
      const tmpTId = tId === undefined ? 0 : tId;
      return this.get('/infoAdmin/' + id + '/' + tmpTId + '?c=' + true + '&t=' + type);
   }

   getDeleteDocument(id: string): any {
      return this.get('/trash/' + id);
   }

   getUserList() {
         return this.get('site/user/activate');
   }

   getInfo(path: string, tin?: string): any {
      const api = tin ? '/work/' + tin + '/path?path=' : '/path?path=';
      return this.get(api + encodeURIComponent(path));
   }

   rename(id: string, newName: string) {
      return this.put('/node/' + id, {name: newName});
   }

   deleteFile(id: string) {
      return this.delete('/node/' + id + '?customerId=' + environment.zdoxCustomerId);
   }

   multipleDeleteFile(delList: any) {
      return this.post('/multiple/node/delete', delList);
   }

   downloadFileUrl(id: string, name?: string): string {
      let url = this.url + '/download?id=' + id + '&customerId=' + environment.zdoxCustomerId;
      if (name) { url += '&name=' + encodeURIComponent(name); }
      return url;
   }

   permanentDelete(file: any) {
      const id = file.id;
      const fileName = file.name;
      return this.delete('/trash/' + id +'/' + fileName);
   }

   multiplePermanentDelete(delList: any) {
      return this.post('multiple/trash/delete', delList);
   }

   deleteAll() {
      return this.delete('trash/delete/all');
   }

   listTypes() {
      return this.get('/types');
   }

   getType(name: string) {
      return this.get('/type/' + name);
   }

   getShareType(name: string) {
      return this.get('/share-public/type/' + name);
   }

   setType(id: string, type: any): Observable<MetadataFormProperty[]> {
      return this.post('/metadata/' + id, type);
   }

   setSignDocType(id: string, type: any, taskId?: any) {
      return this.post('/signdoc/metadata/' + id + '/' + taskId + '/Web', type);
   }

   setMetadata(id: string, metadata: any) {
      return this.put('/metadata/' + id, metadata);
   }

   setSignDocMetadata(id: string, metadata: any, taskId?: any) {
      return this.put('/signdoc/metadata/' + id + '/' + taskId + '/Web', metadata);
   }

   finishUpload(key: string, body: any) {
      return this.http.post(this.url + '/upload/' + key +'?device=Web', body, this.createHttpOptions())
         .pipe(catchError(this.handleErrorUpload()));
   }

   zDoxExportDocument(taskId:string, pathId?: string){
      const body = { taskId:taskId ,token: '', pathId: pathId };
      const api = '/task/export/zdox'
      return this.post(this.url + api, body,{responseType: 'text'});
   }


   oneDriveSearchQuery(access_token: string, searchMessage: string, from?: number,size?: number){
      const body = {token: access_token, searchMessage: searchMessage , from: from , size: size};
      const api = '/onedrive/search/document'
      return this.http.post(this.url + api, body, this.createHttpOptions())
         .pipe(catchError(this.handleErrorOnedrive()));
   }

   onedriveExportDocument(taskId:string,access_token: string, pathId?: string){
      const body = { taskId:taskId ,token: access_token, pathId: pathId };
      const api = '/task/export'
      return this.http.post(this.url + api, body, this.createHttpOptions())
         .pipe(catchError(this.handleErrorOnedrive()));
   }

   /**
    * https://learn.microsoft.com/en-us/onedrive/developer/rest-api/api/driveitem_get_content
    * @param access_token
    * @param id
    * @returns
    */
   oneDriveGetDownloadUrl(access_token: string, id: string): Observable<{"code": number; "message": string}> {
      const body = {token: access_token, id: id};
      const api = '/onedrive/get-download-url'
      return this.http.post(this.url + api, body, this.createHttpOptions())
         .pipe(catchError(this.handleErrorOnedrive(true)));
   }

   oneDriveDownload(access_token: string, id: any,): Observable<Blob>{
      const body = {token: access_token, id: id};
      const api = '/onedrive/download'
      return this.http.post(this.url + api, body, this.createHttpOptions({responseType: 'blob'}))
         .pipe(catchError(this.handleErrorOnedrive(true)));
   }

   oneDriveDownloadByDriveId(access_token: string, id: any,driveId: any,){
      const body = {token: access_token, id: id, driveId:driveId};
      const api = '/onedrive/download-by-driveid'
      return this.http.post(this.url + api, body, this.createHttpOptions({responseType: 'blob'}))
         .pipe(catchError(this.handleErrorOnedrive(true)));
   }

   /**
    * Given a DriveItem 'id', returns all DriveItems under that DriveItem.
    */
   onedriveListItems(access_token: string, id:string, pageSize: number, name?: string): Observable<OneDriveListDriveItemAPIResponse> {
      const body = {token: access_token,id:id, pageSize: pageSize};
      if (name) {
         body['name'] = name;
      }
      const api = '/onedrive/list/items'
      return this.http.post(this.url + api, body, this.createHttpOptions())
         .pipe(catchError(this.handleErrorOnedrive()));
   }

   /**
    * Given a DriveItem 'id', returns a list of all folders under that DriveItem.
    * Non-folder items and files are ignored.\
    * If 'id' is not provided, return folders under the root instead.
    */
   onedriveFolders(access_token: string, pageSize?: number, id?:string) {
      const body = {token: access_token , id:id, pageSize: pageSize};
      const api = '/onedrive/folders'
      return this.http.post(this.url + api, body, this.createHttpOptions())
         .pipe(catchError(this.handleErrorOnedrive()));
   }

   onedriveGetFoldersByName(access_token: string, id?:string, name?:string) {
      const body = {token: access_token , id:id, name:name};
      const api = '/onedrive/get-folders-by-name'
      return this.http.post(this.url + api, body, this.createHttpOptions())
         .pipe(catchError(this.handleErrorOnedrive()));
   }

   /**
    * Returns a list of all DriveItems under the root.
    */
   onedriveList(access_token: string): Observable<OneDriveListDriveItemAPIResponse> {
      const body = {token: access_token};
      const api = '/onedrive/lists'
      return this.http.post(this.url + api, body, this.createHttpOptions())
         .pipe(catchError(this.handleErrorOnedrive()));
   }

   /** Get OneDrive's DriveItem from DriveItem ID */
   onedriveGetItemFromId(access_token: string, id: string): Observable<OneDriveItem> {
      const body = {token: access_token , id:id};
      const api = '/onedrive/get-item-from-id'
      return this.http.post(this.url + api, body, this.createHttpOptions())
         .pipe(catchError(this.handleErrorOnedrive()));
   }

   /**
    * Update DriveItem properties (rename, move, etc.)
    * @see https://learn.microsoft.com/en-us/graph/api/driveitem-update for the proper format of the `updateProperties`.
    */
   onedriveUpdateItem(access_token: string, id: OneDriveItem["id"], updateProperties: OneDriveItem): Observable<OneDriveItem> {
      const body = {token: access_token, id: id, updateProperties: updateProperties};
      const api = '/onedrive/update'
      return this.http.put(this.url + api, body, this.createHttpOptions())
         .pipe(catchError(this.handleErrorOnedrive()));
   }

   /**
    * Delete DriveItem by its ID.
    * @see https://learn.microsoft.com/en-us/onedrive/developer/rest-api/api/driveitem_delete
    * @param access_token OneDrive access token
    * @param id ID of the DriveItem to delete
    * @returns
    */
   onedriveDeleteItem(access_token: string, id: OneDriveItem["id"]): Observable<OneDriveItem> {
      const body = {token: access_token, id: id};
      const api = '/onedrive/delete'
      return this.http.post(this.url + api, body, this.createHttpOptions())
         .pipe(catchError(this.handleErrorOnedrive()));
   }

   onedrive_create(
      access_token: string,
      type: 'document' | 'sheet' | 'presentation',
      name: string,
      parentId: string,
      conflictBehavior: 'rename' | 'replace' | 'fail' = 'rename'
   ) {
      const body = {
         token: access_token,
         type: type,
         name: name,
         parentId: parentId,
         conflictBehavior: conflictBehavior
      };
      return this.http
         .post(this.url + '/onedrive/create', body, this.createHttpOptions())
         .pipe(catchError(this.handleErrorOnedrive()));
   }

   onedrive_createFolder(access_token: string, name: any, parentId: any, fileHandleType: any) {
      const body = {token: access_token, parentFolderId: parentId, folderName: name, fileHandleType: fileHandleType};
      return this.http.post(this.url + '/onedrive/create-folder', body, this.createHttpOptions())
         .pipe(catchError(this.handleErrorOnedrive()));
   }

   uploadFileToOnedrive(access_token: string, folderId: string, fileName:string, maxFileSize: any, fileId: string = undefined) {
      const formData = new FormData();
      formData.append('parentFolderId', folderId);
      formData.append('token', access_token);
      formData.append('fileName', fileName);
      formData.append('fileSize', maxFileSize);
      if(fileId){
         formData.append('fileId', fileId);
      }
      return this.post(this.url + '/onedrive/create-upload-session', formData, {responseType: 'json'})
         .pipe(catchError(this.handleErrorOnedrive()));
   }

   uploadChunkToOneDrive(access_token: string, folderId: string, file: File, uploadUrl: string, maxFileSize: any, startByte: any, endByte: any) {
      const formData = new FormData();
      formData.append('parentFolderId', folderId);
      formData.append('token', access_token);
      formData.append('file', file);
      formData.append('uploadUrl', uploadUrl);
      formData.append('fileSize', maxFileSize);
      formData.append('start', startByte);
      formData.append('end', endByte);
      return this.post(this.url + '/onedrive/upload-file-chunk', formData, {responseType: 'json'});
   }

   saveFile_onedrive(access_token: string, id: string, path: any, name: any, folderId: any) {
      const body = {token: access_token, itemId: id, destination: path, name: name, folderId: folderId};
      return this.post('/onedrive/save', body);
   }

   business_saveFile_onedrive(access_token: string, id: string, path: any, tin: any, name: any) {
      const body = {token: access_token, itemId: id, destination: path, name: name, tin: tin};
      return this.post('/work/' + tin + '/onedrive/save', body);
   }

   saveFile_googledrive(access_token: any, id: any, path: any, folderId: any) {
      const body = {token: access_token, fileId: id, destination: path, folderId: folderId};
      return this.post('/gdrive/save', body);
   }

   business_saveFile_googledrive(access_token: any, id: any, path: any, tin: any) {
      const body = {token: access_token, fileId: id, destination: path, tin: tin};
      return this.post('/work/' + tin + '/gdrive/save', body);
   }

   saveFile_dropbox(access_token: any, id: any, path: any, folderId: any) {
      const body = {
         token: access_token,
         dropboxToken: localStorage.getItem("access_token_dropbox"),
         refreshToken: localStorage.getItem('refresh_token_dropbox'),
         fileId: id,
         destination: path,
         folderId: folderId};
      return this.post('/dropbox/save', body);
   }

   openwith_onedrive(doc_id: any, access_token: any) {
      return this.http.post(this.url + '/onedrive/open', {id: doc_id, token: access_token}, this.createHttpOptions())
         .pipe(catchError(this.handleErrorOnedrive()));
   }

   openwith_google(doc_id: any, access_token: any) {
      return this.http.post(this.url + '/gdrive/open', {id: doc_id, token: access_token}, this.createHttpOptions())
         .pipe(catchError(this.handleErrorOnedrive()));
   }

   edit_onedrive(doc_id: any, item_onedrive_id: any, access_token: any) {
      const body = {id: doc_id, itemId: item_onedrive_id, token: access_token};
      return this.post('/onedrive/checkin', body);
   }

   edit_googledrive(access_token: any, id: any, path: any, doc_id: string) {
      const body = {token: access_token, fileId: id, destination: path, id: doc_id};
      return this.post('/gdrive/edit', body);
   }

   restore(id: any) {
      return this.put('/trash/' + id, null);
   }

   multipleRestore(idList: any) {
      return this.put('/multiple/trash/restore', idList);
   }

   move(fileId: any, folderId: any, tin: any, path: any, fullPath: any) {
      return this.post('/move/' + fileId, {target: folderId, tin: tin, path: path, fullPath: fullPath});
   }

   multipleMove(idList: any, folderId: any, tin: any, path: any, fullPath: any) {
      return this.post('/multiple/move', {idList: idList, target: folderId, tin: tin, fullPath: fullPath});
   }

   list_version(id: any) {
      return this.get('/version/' + id);
   }

   delete_version(id: any, version: any) {
      return this.delete('/version/' + id + '/' + version);
   }

   revert_version(id: any,currentVersion: any, version: any, shared: any) {
      return this.post('/version/' + id + '/' + currentVersion + '/' + version + '?shared=' + shared, null);
   }

   share(docId: any) {
      return this.get('/share/' + docId);
   }

   share_filter(word: any, tin: any) {
      if (tin) {
         return this.get('/people/' + tin + '?filter=' + word);
      } else {
         return this.get('/people/sites?filter=' + word);
      }
   }

   checkValidShareUser(filter: any) {
      return this.get('/checkValidUser?filter=' + filter);
   }

   deleteShare(shareId: any, isSite: boolean,docId:string) {
      return this.delete('/share/' + shareId + '?isSite=' + isSite + '&docId=' + docId);
   }

   share_sendEmail(shareId: any, list_email: any, message: any) {
      return this.post('/share/' + shareId + '/invite', {email: list_email, message: message});
   }

   updateShare(shareId: any, type: any, access: any, download: boolean, expiry: any, list_people: any, password: any, allowShare: any, isSite: any,docId?: any, isNotify?: boolean) {
      const body = {
         docId : docId,
         type: type,
         access: access,
         download: download,
         expiry: expiry,
         permissions: list_people,
         password: password,
         allowShare: allowShare,
         isInherited: type === 'Anyone' || type === 'Private' ? false : true,
         isSite: isSite,
         isNotify: isNotify
      };
      return this.put('/share/' + shareId, body);
   }
   public notifyShareOwner (shared: any, data: any, list_people: any) {
      const body = {
         access: shared.access,
         allowDelete: shared.allowDelete,
         allowShare: shared.allowShare,
         download: shared.download,
         id: shared.id,
         isInherited: shared.isInherited,
         isSite: shared.isSite,
         password: shared.password,
         permissionLevel: shared.permissionLevel,
         sharer: shared.sharer,
         type: shared.type,
         docId: data.id,
         permissions: list_people,
      }
      return this.post('/notify/shareOwner', body, {responseType: 'text'});
   }


   shareComment(shareId: any) {
      return this.get('/share/' + shareId + '/comments');
   }

   postShareComment(shareId: any, content: any) {
      return this.post('/share/' + shareId + '/comments', {content: content});
   }

   sharePublic(shareId: any) {
      return this.get('/share-public/' + shareId);
   }

   share_updateDescription(description: any, shareId: any) {
      return this.put('/share/' + shareId + '/description', {description: description});
   }

   share_setType(shareId: string, type: any) {
      return this.post('/share/' + shareId + '/metadata', type);
   }

   share_setMetadata(shareId: string, metadata: any) {
      return this.put('/share/' + shareId + '/metadata', metadata);
   }

   share_openOnedrive(shareId: any, access_token: any) {
      return this.http.post(this.url + '/share/' + shareId + '/onedrive', {token: access_token}, this.createHttpOptions())
         .pipe(catchError(this.handleErrorOnedrive()));
   }

   share_saveOnedrive(shareId: any, item_onedrive_id: any, access_token: any) {
      const body = {itemId: item_onedrive_id, token: access_token};
      return this.put('/share/' + shareId + '/onedrive', body);
   }

   share_saveGoogledrive(shareId: any, id: any, access_token: any) {
      const body = {token: access_token, fileId: id};
      return this.put('/share/' + shareId + '/googledrive', body);
   }

   getGoogleDriveList(id: any, access_token: any, pageSize: any, pageToken: string) {
      const body = {token: access_token, fileId: id, pageSize: pageSize, pageToken: pageToken};
      return this.post('/googledrive/list', body, this.createHttpOptions({responseType: 'text'}))
         .pipe(catchError(this.handleErrorGoogleDrive(true)));
   }

   uploadFileToGoogleDrive(access_token: string, folderId: string, fileName:string, maxFileSize: any, fileId: string = undefined) {
      const formData = new FormData();
      formData.append('folderId', folderId);
      formData.append('token', access_token);
      formData.append('fileName', fileName);
      formData.append('maxFileSize', maxFileSize);
      if(fileId){
         formData.append('fileId', fileId);
      }
      return this.post(this.url + '/googledrive/upload/file', formData, {responseType: 'json'})
         .pipe(catchError(this.handleErrorGoogleDrive()));
   }

   uploadChunkToGoogleDrive(access_token: string, folderId: string, files: File, uploadUrl: string, maxFileSize: any, startByte: any, endByte: any) {
      const formData = new FormData();
      formData.append('folderId', folderId);
      formData.append('token', access_token);
      formData.append('file', files);
      formData.append('uploadUrl', uploadUrl);
      formData.append('maxFileSize', maxFileSize);
      formData.append('startByte', startByte);
      formData.append('endByte', endByte);
      return this.post(this.url + '/googledrive/upload/chunk', formData, {responseType: 'json'});
   }

   createFolderInGoogleDrive(access_token: string, folderId: string, folderName: string) {
      const body = {token: access_token, folderId: folderId, folderName: folderName};
      return this.post(this.url + '/googledrive/upload/folder', body, {responseType: 'json'})
         .pipe(catchError(this.handleErrorGoogleDrive()));
   }

   createFileInGoogleDrive(access_token: string, folderId: string, fileName: string, mimeType: string) {
      const body = {token: access_token, folderId: folderId, fileName: fileName, mimeType: mimeType};
      return this.post(this.url + '/googledrive/create/file', body, {responseType: 'json'})
         .pipe(catchError(this.handleErrorGoogleDrive()));
   }

   getGoogleDriveSearch(nameSearch: string, access_token: string, pageSize: any, pageToken: string) {
      const body = {token: access_token, nameSearch: nameSearch, pageSize: pageSize, pageToken: pageToken};
      return this.post('/googledrive/search', body, this.createHttpOptions({responseType: 'text'}))
         .pipe(catchError(this.handleErrorGoogleDrive()));
   }

   getFolderFromGoogleDrive(access_token: string, fileName: string, folderId: string) {
      const body = {token: access_token, fileName: fileName, folderId: folderId};
      return this.post('/googledrive/get/folder', body, this.createHttpOptions({responseType: 'json'}))
         .pipe(catchError(this.handleErrorGoogleDrive()));
   }

   getFilesFromGoogleDrive(access_token: string, folderId: string) {
      const body = {token: access_token, folderId: folderId};
      return this.post('/googledrive/get/file', body, this.createHttpOptions({responseType: 'json'}))
         .pipe(catchError(this.handleErrorGoogleDrive()));
   }

   deleteGoogleDriveFile(id: any, access_token: any) {
      return this.delete('/googledrive/delete?fileId=' + id + '&token=' + access_token);
   }

   googleDriveExportDocument(taskId:string,access_token: string, pathId?: string){
      const body = { taskId:taskId ,token: access_token, pathId: pathId };
      const api = '/task/export/googledrive'
      return this.http.post(this.url + api, body, this.createHttpOptions({responseType: 'text'}))
         .pipe(catchError(this.handleErrorGoogleDrive()));
   }

   googleDriveGetItemFromId(access_token: string, fileId: string){
      const body = {token: access_token , fileId:fileId};
      const api = '/googledrive/get';
      return this.post(this.url + api, body, this.createHttpOptions({responseType: 'text'}))
         .pipe(catchError(this.handleErrorGoogleDrive()));
   }

   moveFileToGoogleDrive(fileId: any, newFolderId: any, oldFolderId: any, access_token: any) {
      const body = {fileId: fileId, folderId: newFolderId, previousFolderId: oldFolderId, token: access_token};
      const api = '/googledrive/moveFileToFolder';
      return this.post(this.url + api, body, this.createHttpOptions({responseType: 'json'}))
         .pipe(catchError(this.handleErrorGoogleDrive()));
   }

   downloadGoogleDrive(fileId: string, mimeType: string, access_token: string) {
      const body = {fileId: fileId, mimeType: mimeType, token: access_token};

      return this.post(this.url + '/googledrive/download', body, {responseType: 'blob'})
         .pipe(catchError(this.handleErrorGoogleDrive()));
   }

   updateFileGoogleDrive(fileId: string, newName: string, access_token: string){
      const body = {fileId: fileId, newName: newName, token: access_token};

      return this.put('/googledrive/update', body)
         .pipe(catchError(this.handleErrorGoogleDrive()));
   }

   getMediaFileGoogleDrive(fileId: string, mimeType: string, access_token: string) {
      const body = {fileId: fileId, mimeType: mimeType, token: access_token};

      return this.post(this.url + '/googledrive/getMediaFile', body, {responseType: 'blob'})
         .pipe(catchError(this.handleErrorGoogleDrive()));
   }

   getComment(id: any) {
      return this.get('/comments/' + id);
   }

   getShareComment(id: any) {
      return this.get('/share-public/comments/' + id);
   }

   postComment(id: any, content: any, taskId?: any) {
      if(taskId) {
         return this.post('/comments/' + id + '?taskId=' + taskId, {content: encodeURIComponent(content)});
      } else {
         return this.post('/comments/' + id, {content: encodeURIComponent(content)});
      }
   }

   deleteComment(comment_id: any) {
      return this.delete('/comments/' + comment_id);
   }

   renameGroup(id: any, name: any, siteName: any, tin: any) {
      const body = {
         displayName: name
      };
      return this.put('/site/group/' + id + '?device=Web&siteName=' + siteName + '&tin= ' + tin, body);
   }

   upgradeEtax(tin: any, name: any, username: any, password: any, server: any, port: any, files: File[]) {
      const data = {
         tin: tin,
         name: name,
         username: username,
         password: password,
         server: server,
         port: port
      };
      const formData = new FormData();
      formData.append('data', new Blob([JSON.stringify(data)], {type: 'application/json'}));
      for (let i = 0; i < files.length; i++) {
         formData.append('file', files[i]);
      }
      return this.post('/upgrade/?device=Web', formData);
   }

   search(q: string, skip: number, take: number, suggest?: boolean, highlight?: boolean, selectFilter?: boolean) {
      let api = '/search?q=' + q + '&skip=' + skip + '&take=' + take;
      if (suggest) {
         api += '&suggest=true';
      }
      if (highlight) {
         api += '&highlight=true';
      }
      if (selectFilter) {
         api += '&selectFilter=true';
      }
      return this.get(api);
   }

   changePassword(oldpw: any, newpw: any) {
      return this.post('/changepw?device=Web', {'oldpw': oldpw, 'newpw': newpw});
   }

   setPassword(newpw: any, email?: any) {
      this.browserInfo = this.getBrowserInfo();
      if (email) {
         return this.post('/setpwsite?userName=' + email + '&device=Web&browserInfo=' + this.browserInfo + '&location=' + this.location, {'newpw': newpw});
      } else {
         return this.post('/setpw?device=Web&browserInfo=' + this.browserInfo + '&location=' + this.location, {'newpw': newpw});
      }
   }

   forgotPassword(email: any) {
      return this.post('/forgotpw?u=' + email + '&device=Web', null, {responseType: 'text'});
   }

   resetPassword(newpass: any, forgotkey: any) {
      return this.post('/resetpw/' + forgotkey + '?device=Web', {password: newpass});
   }

   isFisrtLogin(key: any) {
      return this.get('/isfirstlogin/' + key);
   }

   resendEmail(email: any) {
      return this.post('/signup/resend?u=' + email + '&device=Web', null, {responseType: 'text'});
   }

   setTypeandProperties(doc_id: any, content: any, prop_list: any) {
      return this.post('/metadata/' + doc_id, {'type': content, 'properties': prop_list});
   }

   updateProfile(firstName: string, lastName: string, enableAuthLogin: boolean, enableAuthSign: boolean,
                 enableTwoFactorLoginGoogle: boolean, enableTwoFactorSignGoogle: boolean, verificationGoogle: boolean,
                 isNotifyTask: boolean, isNotifyTaskCompleted: boolean, includeName: boolean, includeDate: boolean) {
      return this.post('/profile/update?device=Web',
         {
            'firstName': firstName,
            'lastName': lastName,
            'enableAuthLogin': enableAuthLogin,
            'enableAuthSign': enableAuthSign,
            'enableTwoFactorLoginGoogle': enableTwoFactorLoginGoogle,
            'enableTwoFactorSignGoogle': enableTwoFactorSignGoogle,
            'verificationGoogle': verificationGoogle,
            'enableNotifyTask': isNotifyTask,
            'enableNotifyTaskCompleted': isNotifyTaskCompleted,
            'includeName': includeName,
            'includeDate': includeDate
         },
         {responseType: 'text'});
   }

   updateName(firstName: string, lastName: string){
      return this.post('/profile/update/name?device=Web',
      {
         'firstName': firstName,
         'lastName': lastName
      },
      {responseType: 'text'});
   }

   updateEmail(email: string){
      return this.post('/profile/update/email?device=Web',
      {
         'email': email
      },
      {responseType: 'text'});
   }


   updateSettingSignature(includeName: boolean, includeDate: boolean) {
      return this.post('/profile/update/setting-signature?device=Web',
      {
         'includeName': includeName,
         'includeDate': includeDate
      },
      {responseType: 'text'});
      }

   uploadSignatue(signatureId: any, sType: string, file?: any) {
      const formData = new FormData();
      formData.append('file', file);
      return this.post('/signature/upload/' + signatureId + '?device=Web&signType=' + sType, formData, {responseType: 'text'});
   }

   uploadExtSignature(key: any, sType: string, file?: any) {
      const formData = new FormData();
      formData.append('file', file);
      return this.post('/signature/upload/ext/' + key + '?device=Web&signType=' + sType, formData, {responseType: 'text'});
   }

   getProfile() {
      return this.get('/profile');
   }

   getProfileByUserName(userName: any) {
      const body_data = {
         userName: userName
      };
      return this.post('/profile', body_data, {responseType: 'json'});
   }

   getContactAll(pagesize: any, page: any, sort?: any, order?: any, kw?: any, take?: any) {
      return this.get('/contact/manage/list?sort=' + sort + '&order=' + order + '&pagesize=' + pagesize + '&page=' + page + '&kw=' + kw + '&take=' + take);
   }

   getContactAllShare(pagesize: any, page: any, sort?: any, order?: any, kw?: any, take?: any) {
      return this.get('/contact/manage/listShare?sort=' + sort + '&order=' + order + '&pagesize=' + pagesize + '&page=' + page + '&kw=' + kw + '&take=' + take);
   }

   getContactShare(kw?: any) {
      return this.get('/contact/listAll?kw=' + kw);
   }

   getContactCurrentLevelNS(taskId?: any, level?: any) {
      return this.get('/signatory/list/' + taskId + '/' + level + '/true');
   }

   getContactCurrentLevelAll(taskId?: any, level?: any) {
      return this.get('/signatory/list/' + taskId + '/' + level + '/false');
   }

   getContact() {
      return this.get('/contact/list');
   }

   getContactInGroup(groupId: any) {
      return this.get('/contact/group/' + groupId);
   }

   addContact(contactName: string, contactEmail: string) {
      const body_data = {
         contactName: contactName,
         contactEmail: contactEmail
      };

      return this.post('/contact/add', body_data, {responseType: 'text'});
   }

   activeContact(contactId: number, contactEmail: string) {
      return this.post('/contact/active/' + contactId, {contactEmail: contactEmail}, {responseType: 'text'});
   }

   inactiveContact(contactId: number, contactEmail: string) {

      return this.post('/contact/inactive/' + contactId, {contactEmail: contactEmail}, {responseType: 'text'});
   }

   updateContact(contactId: number, contactName: string, contactEmail: string) {
      const body_data = {
         contactId: contactId,
         contactName: contactName,
         contactEmail: contactEmail
      };

      return this.post('/contact/update', body_data, {responseType: 'text'});
   }

   deleteContact(contactIdList: any) {

      return this.post('/contact/delete', contactIdList, {responseType: 'text'});
   }

   deleteUserCompany(userList: any, tin: any, siteName: any) {

      return this.post('/work/' + tin + '/removes?device=Web&siteName=' + siteName, userList);
   }

   deleteUserSite(userList: any) {

      return this.post('/work/removes?device=Web', userList);
   }

   activeMember(username: any,isActive: any) {
      return this.put("/work/active/member/" + username + "/" + isActive);
   }

   addCompanyGroup(groupName: any, permission: any, tin: any, listMember: any, siteName: any) {
      const body_data = {
         siteGroup: {
            groupName: groupName,
            siteCode: tin,
            permission: permission
         }, members: listMember
      };
      return this.post('/site/create/group?device=Web&siteName=' + siteName, body_data, {responseType: 'text'});
   }

   listCompanyGroup(tin: any, filter: any) {
      return this.get('work/' + tin + '/company/team?filter=' + filter);
   }

   listCompanyGroupWithUsername(tin: any, filter: string, usernames: string[]) {
      return this.post('work/' + tin + '/company/groups?filter=' + filter,usernames);
   }

   getCompanyGroup(tin: any) {
      return this.http.post('/site/team/', {siteCode: tin});
   }

   getGroupAll(pagesize: number, page: number, sort?: any, order?: any, kw?: any, take?: any) {
      return this.get('/group/manage/list?sort=' + sort + '&order=' + order + '&pagesize=' + pagesize + '&page=' + page + '&kw=' + kw + '&take=' + take);
   }

   getGroupAllShare(pagesize: number, page: number, sort?: any, order?: any, kw?: any, take?: any) {
      return this.get('/group/manage/listShare?sort=' + sort + '&order=' + order + '&pagesize=' + pagesize + '&page=' + page + '&kw=' + kw + '&take=' + take);
   }

   getGroupShare(kw?: any) {
      return this.get('/group/listAll?kw=' + kw);
   }

   getGroup() {
      return this.get('/group/list');
   }

   addGroup(groupId: number, groupName: string, contactList: any) {
      const body_data = {
         groupId: groupId,
         groupName: groupName,
         contactList: contactList
      };

      return this.post('/group/add', body_data, {responseType: 'text'});
   }

   activeGroup(groupId: number, groupName: string) {
      return this.post('/group/active/' + groupId, {groupName: groupName}, {responseType: 'text'});
   }

   inactiveGroup(groupId: number, groupName: string) {

      return this.post('/group/inactive/' + groupId, {groupName: groupName}, {responseType: 'text'});
   }

   updateGroup(groupId: number, groupName: string, contactList: any) {
      const body_data = {
         groupId: groupId,
         groupName: groupName,
         contactList: contactList
      };

      return this.post('/group/update', body_data, {responseType: 'text'});
   }

   deleteGroup(groupIdList: any) {

      return this.post('/group/delete', groupIdList, {responseType: 'text'});
   }

   getWorkflowAll(pagesize: number, page: number, sort?: any, order?: any, kw?: any, take?: any) {
      return this.get('/workflow/manage/list?sort=' + sort + '&order=' + order + '&pagesize=' + pagesize + '&page=' + page + '&kw=' + kw + '&take=' + take);
   }

   getWorkflowShare(pagesize: number, page: number, sort?: any, order?: any, kw?: any, take?: any , all?: any) {
      return this.get('/workflow/manage/listShare?sort=' + sort + '&order=' + order + '&pagesize=' + pagesize + '&page=' + page + '&kw=' + kw + '&take=' + take + '&all=' + all);
   }

   listWorkflow(listShare: boolean) {
      return this.get('/workflow/list?listShare='+ listShare);
   }

   getWorkflow(workflowId: number) {
      return this.get('/workflow/get/' + workflowId);
   }

   saveWorkflow(workflowId: number, workflowName: string, apvLevel: any) {
      const body_data = {
         workflowId: workflowId,
         workflowName: workflowName,
         apvLevel: apvLevel
      };
      return this.post('/workflow/save', body_data, {responseType: 'text'});
   }

   activeWorkflow(workflowId: number, workflowName: string) {
      return this.post('/workflow/active/' + workflowId, {workflowName: workflowName}, {responseType: 'text'});
   }

   inactiveWorkflow(workflowId: number, workflowName: string) {
      return this.post('/workflow/inactive/' + workflowId, {workflowName: workflowName}, {responseType: 'text'});
   }

   deleteWorkflow(workflowIdList: any) {
      return this.post('/workflow/delete', workflowIdList, {responseType: 'text'});
   }

   getTemplateAll(pagesize: number, page: number, sort?: any, order?: any, kw?: any, take?: any) {
      return this.get('/template/manage/list?sort=' + sort + '&order=' + order + '&pagesize=' + pagesize + '&page=' + page + '&kw=' + kw + '&take=' + take);
   }

   getTemplateShare(pagesize: number, page: number, sort?: any, order?: any, kw?: any, take?: any , all?: any) {
      return this.get('/template/manage/listShare?sort=' + sort + '&order=' + order + '&pagesize=' + pagesize + '&page=' + page + '&kw=' + kw + '&take=' + take + '&all=' + all);
   }

   listTemplate(listShare: boolean, siteId?: any) {
      return this.get('/template/list?listShare=' + listShare + (siteId ? '&siteId=' + siteId : ''));
   }

   getTemplate(templateId: number, share?: any) {
      let api = '/template/get/' + templateId;
      if (share) {
         api += '?isShare=' + share;
      }
      return this.get(api);
   }

   saveTemplate(templateId: number, workflowId: number, taskAttribute: TaskAttribute) {
      const body_data = {
         dayLeft: taskAttribute.dayLeft,
         retentionPeriod: taskAttribute.retentionPeriod,
         templateId: templateId,
         templateName: taskAttribute.taskName,
         workflowId: workflowId,
         docList: taskAttribute.docList,
         link: taskAttribute.exportPath,
         idFolderExport: taskAttribute.idFolderExport,
         siteId: taskAttribute.siteId

      };
      return this.post('/template/save', body_data, {responseType: 'text'});
   }

   activeTemplate(templateId: number, templateName: string) {
      return this.post('/template/active/' + templateId, {templateName: templateName}, {responseType: 'text'});
   }

   inactiveTemplate(templateId: number, templateName: string) {
      return this.post('/template/inactive/' + templateId, {templateName: templateName}, {responseType: 'text'});
   }

   deleteTemplate(templateIdList: any) {
      return this.post('/template/delete', templateIdList, {responseType: 'text'});
   }

   getAutoApproveAll(pagesize: number, page: number, sort?: any, order?: any, kw?: any, take?: any) {
      return this.get('/auto-approve/list?sort=' + sort + '&order=' + order + '&pagesize=' + pagesize + '&page=' + page + '&kw=' + kw + '&take=' + take);
   }

   enableAutoApprove(templateId: number, workflowId: number, templateName: string) {
      return this.post('/auto-approve/enable/' + templateId + '/' + workflowId, {templateName: templateName}, {responseType: 'text'});
   }

   disableAutoApprove(templateId: number, templateName: string) {
      return this.post('/auto-approve/disable/' + templateId, {templateName: templateName}, {responseType: 'text'});
   }

   uploadDocumentTemp(type: string, body: any, fileNo: any, key: string) {
      if (type === 'startFlow') {
         type = 'task';
      } else if (type === 'attachImage') {
         type = 'attachimage'
      }
      return this.post('/' + type + '/upload/' + fileNo + '/' + key, body, {responseType: 'json'});
   }

   uploadThumbnailDocument(body: any, docId: any, insertPage: any, maxPage: any, key: string, taskId?: any) {
      return this.post('thumbnail/insert/' + docId + '/' + insertPage + '/' + maxPage + '/' + key + '/' + taskId + '/Web',
         body, {responseType: 'json'});
   }

   deleteThumbnailDocument(docId: any, selectPageDelete: any, lenDoc: any, maxPage: any, taskId: any) {
      const formData = new FormData();
      formData.append('selectPageDelete', new Blob([JSON.stringify(selectPageDelete)], {type: 'application/json'}));
      return this.post('thumbnail/delete/' + docId + '/' + lenDoc + '/' + maxPage + '/' + taskId + '/Web',
         formData, {responseType: 'json'});
   }

   deleteAttachFile(docId: any, taskId: any) {
      return this.post('attach-file/delete/' + docId + '/' + taskId + '/Web', {responseType: 'json'});
   }

   rotateThumbnailDocument(docId: any, rotateDirection: any, selectPageRotate: any, taskId: any) {
      return this.post('thumbnail/rotate/' + docId + '/' + rotateDirection + '/' + taskId + '/Web',
         selectPageRotate, {responseType: 'json'});
   }

   rotateDocument(itemRotates: any, isFilter?: boolean, taskId?: any) {
      return this.post('thumbnail/rotate-document/' + isFilter + '/' + taskId + '/Web', itemRotates, {responseType: 'json'});
   }

   deleteDocument(id: string) {
      return this.post('/file/del/' + id, null, {responseType: 'text'});
   }

   deleteGroupCompany(groupList: any, siteName: any, tin: any) {
      return this.post('/site/group/delete?device=Web&siteName=' + siteName + '&tin=' + tin, groupList);
   }

   getMembercompany(id_group: any) {
      return this.get('/site/group-user/' + id_group);
   }

// /admin/group-user/{id_group}/{emailUser} //delete method delete
   deleteMemberGroup(idGroup: string, email: string) {
      return this.delete('/site/group-user/' + idGroup + '/' + email);
   }

   addMemberGroup(idGroup: string, email: string) {
      return this.post('/site/group-user/' + idGroup + '/' + email + '?device=Web');
   }

   /**
    * async = true: ไฟล์ที่ยังแปลงเป็น PDF ไม่เสร็จจะได้ converting = true กลับมาทันที (ไม่รอแปลง)
    * ใช้คู่กับ PdfRenditionWatcher เพื่อ poll ไฟล์ที่ยังแปลงอยู่
    */
   transformPdf(docIdList: any, taskId?: number, async = false) {
      const params = [];
      if (taskId != null) {
         params.push('taskId=' + taskId);
      }
      if (async) {
         params.push('async=true');
      }
      return this.post('/pdftransform' + (params.length ? '?' + params.join('&') : ''), docIdList, {responseType: 'json'});
   }

   /** แจ้ง backend ว่าไม่ต้องรอเอกสารเหล่านี้แล้ว (ออกจากหน้า/cancel) ให้เอาออกจากคิวแปลง */
   releasePdfTransform(docIdList: string[]) {
      // เรียก http ตรง ไม่ผ่าน handleError: เรียกตอนออกจากหน้า/logout ถ้าได้ 401 ห้ามพาไปหน้า login หรือขึ้น toast
      this.http.post(this.getUrl('/pdftransform/release'), JSON.stringify(docIdList),
         this.createHttpOptions({responseType: 'text'}, true)).subscribe({error: () => {}});
   }

   transformPdfThumbnails(docIdList: any, dpi: any, taskId?: number, async = false) {
      const params = [];
      if (taskId) {
         params.push('taskId=' + taskId);
      }
      if (async) {
         params.push('async=true');
      }
      return this.post('/pdftransformDpi/' + dpi + (params.length ? '?' + params.join('&') : ''), docIdList, {responseType: 'json'});
   }

   getDocByPage(docId: string, page: number, size?: number) {
      return this.get('/document/' + docId + '/' + page);
   }

   signDocument(taskId: number, data: any, password?: string, code?: string, taskForm?: any) {
      const body_data = {
         taskId: taskId,
         password: password,
         docList: data,
         code: code,
         form: taskForm ?? null
      };
      return this.post('/signdoc/Web', body_data, {responseType: 'text'});
   }

   sendTask(taskAttribute: any, isDocList: boolean, originalId: any, originalPath: any, formInfo?: any) {
      const body_data = {
         taskId: taskAttribute.taskId,
         taskName: taskAttribute.taskName,
         dueDate: taskAttribute.dueDate,
         priority: taskAttribute.priority,
         templateId: taskAttribute.templateId,
         workflowId: taskAttribute.workflowIdSelect,
         currentLevel: taskAttribute.currentLevel,
         apvLevel: taskAttribute.apvLevel,
         docList: taskAttribute.docList,
         message: taskAttribute.message,
         idFolderExport: taskAttribute.idFolderExport,
         exportStatus: taskAttribute.exportStatus,
         isAttachFile: taskAttribute.isAttachFile,
         originalId: originalId,
         originalPath: originalPath,
         siteId: taskAttribute.siteId,
         certCode: taskAttribute.certCode,
         retentionPeriod: taskAttribute.retentionPeriod,
         tempId: taskAttribute.tempId,
         typeName: taskAttribute.typeName,
         formInfo: formInfo ? formInfo : undefined
      };
      return this.post('/task/send/' + isDocList + '/Web', body_data, {responseType: 'text'});
   }

   saveTask(taskAttribute: any) {
      const body_data = {
         taskId: taskAttribute.taskId,
         taskName: taskAttribute.taskName,
         dueDate: taskAttribute.dueDate,
         priority: taskAttribute.priority,
         templateId: taskAttribute.templateId,
         workflowId: taskAttribute.workflowIdSelect,
         currentLevel: taskAttribute.currentLevel,
         apvLevel: taskAttribute.apvLevel,
         docList: taskAttribute.docList,
         message: taskAttribute.message,
         idFolderExport: taskAttribute.idFolderExport,
         exportStatus: taskAttribute.exportStatus,
         siteId: taskAttribute.siteId,
         certCode: taskAttribute.certCode,
         retentionPeriod: taskAttribute.retentionPeriod,
         tempId: taskAttribute.tempId
      };
      return this.post('/task/save/Web', body_data, {responseType: 'text'});
   }

   saveTaskAttachFile(taskAttribute: TaskAttribute, docId: string) {
      const body_data = {
         taskId: taskAttribute.taskId,
         taskName: taskAttribute.taskName,
         dueDate: taskAttribute.dueDate,
         priority: taskAttribute.priority,
         templateId: taskAttribute.templateId,
         workflowId: taskAttribute.workflowIdSelect,
         currentLevel: taskAttribute.currentLevel,
         apvLevel: taskAttribute.apvLevel,
         docList: taskAttribute.docList,
         message: taskAttribute.message,
         idFolderExport: taskAttribute.idFolderExport,
         exportStatus: taskAttribute.exportStatus,
         retentionPeriod: taskAttribute.retentionPeriod
      };
      return this.post('/attachfiletask/save/' + docId + '/Web', body_data, {responseType: 'text'});
   }

   rejectTask(taskId: number, rejectReason: string) {
      const body_data = {
         rejectReason: rejectReason
      };
      return this.post('/task/reject/' + taskId + '/Web', body_data, {responseType: 'text'});
   }

   resendTask(taskId: number) {
      return this.post('/task/re-send/' + taskId + '/Web', null, {responseType: 'text'});
   }

   remindTask(taskId: number) {
      return this.post('/task/remind/' + taskId, null, {responseType: 'text'});
   }

   deleteTask(task: any) {
      const body_data = {
         taskId: task.taskId,
         taskName: task.taskName
      };
      return this.post('/task/delete/Web', body_data, {responseType: 'text'});
   }

   restoreTask(task: any) {
      const body_data = {
         taskId: task.taskId,
         taskName: task.taskName
      };
      return this.post('/task/restore/Web', body_data, {responseType: 'text'});
   }

   deleteTaskInTrash(task: any) {
      const body_data = {
         taskId: task.taskId,
         taskName: task.taskName
      };
      return this.post('/task/trash/delete', body_data, {responseType: 'text'});
   }

   openTask(taskId: number) {
      return this.get('/task/open/' + taskId);
   }

   getTask(taskId: number, isInsetLog: boolean): Observable<TaskAttribute_Response> {
      return this.get('/task/get/' + taskId + '/Web' + '/' + isInsetLog);
   }

   deleteDocList(taskId: number) {
      return this.post('/document/delete/' + taskId + '/Web');
   }
   closeTask(taskId: number) {
      return this.put('task/close/' + taskId + '/Web');
   }

   getCompleteTask(docId: string) {
      return this.get('task/complete-task/' + docId);
   }

   revertVersionTask(id: any, version: any, taskId: any) {
      return this.post('/task/version/' + id + '/' + version + '/' + taskId + '/Web', null);
   }

   revertVersionTemplate(id: any, version: any) {
      return this.post('/task/version/' + id + '/' + version, null);
   }

   listInboxSummary(fromDate: any, toDate: any) {

      return this.get('/task/listInboxSummary?fromDate=' + fromDate + '&toDate=' + toDate + '&device=Web');
   }

   checkQuotaTask() {
      return this.get('/task/checkQuotaTask');
   }

   getTaskDownloadLimit() {
      return this.get('/task/config/downloadLimit');
   }

   listTask(taskStatus: string, fromDate: any, toDate: any, pagesize: number, page: number,
            textSearch: string, take: any, id: string, sort?: any, order?: any) {

      return this.get('/task/list?status=' + taskStatus + '&fromDate=' + fromDate + '&toDate=' + toDate +
         '&sort=' + sort + '&order=' + order + '&pagesize=' + pagesize + '&page=' + page + '&device=Web' +
         '&textSearch=' + textSearch + '&take=' + take + '&id=' + id);
   }

   listTaskSearchAdvanced(textSearch: any, pagesize: number, page: number, sort?: any, order?: any) {
      const body = {
         taskName: textSearch.taskName,
         statusSearch: textSearch.statusSearch,
         emailSender: textSearch.emailSender,
         emailSigner: textSearch.emailSigner,
         sentDateFrom: textSearch.sentDateFrom,
         sentDateTo: textSearch.sentDateTo,
         lastUpdateFrom: textSearch.lastUpdateFrom,
         lastUpdateTo: textSearch.lastUpdateTo
      };
      return this.post('/task/searchAdvanced?sort=' + sort + '&order=' + order + '&pagesize=' + pagesize + '&page=' + page + '&device=Web', body, {responseType: 'text'});
   }

   reAssign(reassignTo: any, nameReassignTo: any, data: any) {
      const taskReassign = {
         reassignTo: reassignTo,
         nameReassignTo: nameReassignTo,
         data: data
      };
      return this.post('/task/reassign/Web', taskReassign, {responseType: 'text'});
   }

   senderReAssign(reassignTo: any, nameReassignTo: any, reassignFrom: any, data: any) {
      const taskSenderReassign = {
         reassignTo: reassignTo,
         nameReassignTo: nameReassignTo,
         reassignFrom: reassignFrom,
         data: data
      };
      return this.post('/task/sender-reassign/Web', taskSenderReassign, {responseType: 'text'});
   }

   checkReassign(reassignTo: any, taskId: any) {
      return this.get('/task/check-reassign?reassignTo=' + reassignTo + '&taskId=' + taskId);
   }

   logReassign(taskId: number) {
      return this.get('/task/log-reassign/Web?taskId=' + taskId);
   }

   insertLogActivityTask(taskId, userName, taskAction, channelAccess) {
      const body_data = {
         taskId: taskId,
         emailUserAction: userName,
         taskAction: taskAction,
         channelAccess: channelAccess
      };
      return this.post('/task/insert-history-task/', body_data);
   }

   getHistoryActivityTask(taskId: number) {
      return this.get('/task/history-task/' + taskId);
   }

   consentPdpa() {
      return this.get('/consentPdpa');
   }

   updatePdpa(consentRegister: boolean, consentEkyc: boolean, consentNdid: boolean) {
      return this.post('/pdpa/accept?consentRegister=' +  consentRegister + '&consentEkyc=' + consentEkyc + '&consentNdid=' + consentNdid, null, {responseType: 'text'});
   }

   registerPdpa(userName: string) {
      return this.post('/updatePdpaRegister?user=' + userName , null , {responseType: 'text'});
   }

   getWatermark(device: string, siteCode?: string) {
      let url = '/watermark?device=' + device;
      if (siteCode) {
         url += '&siteCode=' + siteCode;
      }
      return this.get(url);
   }

   getWatermarkBySite(siteCode: string) {
      return this.get('/getWatermark/' + siteCode);
   }

   updateWatermark(siteCode: string, body: any) {
      return this.put('/watermark/update/' + siteCode, body);
   }

   deleteWatermark(siteCode: string) {
      return this.delete('/watermark/delete/' + siteCode);
   }

   // ─── IP / Client-ID allow lists (scoped by current site) ────────────────────
   // Returns whether the current site has a customer id mapped (ext_customer_site);
   // a site with none has no allow list and the menu is hidden.
   isAllowListEnabled(siteCode: string) {
      return this.get('/site/' + siteCode + '/allow-list/enabled');
   }

   listAllowedIp(siteCode: string, params?: any) {
      return this.get('/site/' + siteCode + '/allowed-ip', {params});
   }

   createAllowedIp(siteCode: string, body: any) {
      return this.post('/site/' + siteCode + '/allowed-ip', body);
   }

   updateAllowedIp(siteCode: string, id: number, body: any) {
      return this.put('/site/' + siteCode + '/allowed-ip/' + id, body);
   }

   deleteAllowedIp(siteCode: string, id: number) {
      return this.delete('/site/' + siteCode + '/allowed-ip/' + id);
   }

   listAllowedClient(siteCode: string, params?: any) {
      return this.get('/site/' + siteCode + '/allowed-client', {params});
   }

   createAllowedClient(siteCode: string, body: any) {
      return this.post('/site/' + siteCode + '/allowed-client', body);
   }

   updateAllowedClient(siteCode: string, id: number, body: any) {
      return this.put('/site/' + siteCode + '/allowed-client/' + id, body);
   }

   deleteAllowedClient(siteCode: string, id: number) {
      return this.delete('/site/' + siteCode + '/allowed-client/' + id);
   }

   saveCommentTask(taskId: number, docId: string, data: any) {
      return this.post('/task/comment/save/' + taskId  + '/' + docId + '/Web', data);
   }

   updateCommentTask(data: any) {
      return this.post('/task/comment/update/', data, {responseType: 'text'});
   }

   updateAttachImage(data: any) {
      return this.post('/task/attach/update/', data, {responseType: 'text'})
   }

   deleteCommentTask(taskId: any, id) {
      return this.delete('/task/comment/delete/' + taskId  + '/' + id + '/Web');
   }

   saveReplyTask(taskId: any, signItemId: any, postId: any, CommentInfo) {
      return this.post('/task/reply/save/' + taskId  + '/' + signItemId + '/' + postId + '/Web', CommentInfo, {responseType: 'text'});
   }

   getBillingPeriod(lang: string) {
      return this.get('/company/period/list?lang=' + lang);
   }

   getSeller(lang: string) {
      return this.get('/company/seller/list?lang=' + lang);
   }

   private async json(res: Response) {
      const contentType = res.headers.get('content-type');
      if (contentType != null && contentType.indexOf('application/json') === 0) {
         try {
            return await res.json() || {};
         } catch (e) {
            return res.text();
         }
      }
      return res.text();
   }

   public openTrash(skip: number, take: number) {
      return this.get('/trash?skip=' + skip + '&take=' + take);
   }

   public getContentAnnotation(annotationId: any, version: any) {
      return this.get('/content/version?id=' + annotationId + '&version=' + version + '&isAnnotation=true', {responseType: 'text'});
   }

   public insertDataToDatabase(docId: string, annotationId: string, annotationVersion: string, createdBy: string) {
      const body = {
         docId : docId,
         annotationId: annotationId,
         annotationVersion: annotationVersion,
         createdBy: createdBy
      };
      return this.post('/doc-annotation/save', body, {responseType: 'text'});
   }

   public upDateDataToDatabase(annotationVersion: string, docId: string, annotationId: number, createdBy: string) {
      const body = {
         annotationVersion: annotationVersion,
         docId: docId,
         docAnnotationId: annotationId,
         createdBy: createdBy
      };
      return this.post('/doc-annotation/update', body, {responseType: 'text'});
   }

   public notifyDocInfo(docId: string, fileName: string, userName: string, userEmail: string) {
      const body = {
         docId: docId,
         fileName: fileName,
         userName: userName,
         userEmail: userEmail
      }
      return this.post('/pdftron/notify', body, {responseType: 'text'});
   }

   public getAnnotation(docId: string, isLast?: boolean): Observable<AnnotationAttributes> {
      if (docId) {
         let last = '';
         if (isLast) last = '?last=true';
         return this.get('/annotation/' + docId + last);
      } else {
         return;
      }
   }

   public getMapVersionAnnotation(docId: string, docVersion: string) {
      if (docId !== null && docVersion !== null) {
         return this.get('/doc-annotation/map-version/' + docId + '/' + docVersion);
      } else {
         return;
      }
   }

   public getCheckTimeOut() {
      return this.get('/check/timeout');
   }

   public getCertification() {
      return this.get('/cert/list');
   }

   public uploadCertification(file: File, pass: string, type: string, tin: string, id: string, privateKey: string) {

      if (!privateKey) {
         privateKey = pass;
      }
      const body = new FormData();
      body.append('file', file);
      body.append('pass', pass);
      body.append('type', type);
      body.append('tin', tin);
      body.append('id', id);
      body.append('privateKey', privateKey)
      return this.post('/cert/upload', body);
   }

   private handleErrorOnedrive(flagDownload?:boolean) {

      return (error: any): Observable<any> => {
         let errMsg: string;
         let status: number;
         if (error instanceof HttpErrorResponse) {

            status = error.status;

            if(error.error.message){
               errMsg = error.error.message
            }else if(typeof error.error === 'string'){
               errMsg = error.error
            }else{
               errMsg = 'INTERNAL_ERROR'
            }

            const path = location.pathname;
            if ((status === 401 && error.error.code && error.error.innerError) || (status === 401 && flagDownload) || ((status === 401 || status === 500) && error.error.message === 'Stream closed')){
               localStorage.removeItem('access_token');
               return throwError(error);
            }

            if (status === 401){
               localStorage.removeItem('access_token');
               this.translate.get('timeout').subscribe(s => {
                  this.toast.error(s);
               });
               const pathUrl = Path.combine('/login', path);
               this.router.navigate([pathUrl]);
            }

            return throwError(new Error(status, errMsg));
         } else {
            errMsg = error.message || error.toString();
         }
         return throwError(new Error(error.status, errMsg));
      };
   }

   private handleErrorGoogleDrive(flagDownload?:boolean) {

      return (error: any): Observable<any> => {
         let errMsg: string;
         let status: number;
         status = error.status;
         const path = location.pathname;

         if (error instanceof HttpErrorResponse) {

            if(error.error.message){
               errMsg = error.error.message
            }else if(typeof error.error === 'string'){
               if (!error.error.includes('invalid_request')) {
                  errMsg = error.error
               }
            }else{
               errMsg = 'INTERNAL_ERROR'
            }

            if ((status === 401 && flagDownload)){
               sessionStorage.removeItem('access_token_google');
               return throwError(new Error(status, errMsg));
            }

            if (status === 401){
               sessionStorage.removeItem('access_token_google');
               this.translate.get('timeout').subscribe(s => {
                  this.toast.error(s);
               });
            }

            return throwError(new Error(status, errMsg));
         } else {
            errMsg = error.message || error.toString();
            if (status === 401){
               sessionStorage.removeItem('access_token_google');
               this.translate.get('timeout').subscribe(s => {
                  this.toast.error(s);
               });
            }
         }
         return throwError(new Error(error.status, errMsg));
      };
   }

   private handleErrorDropBox(flagDownload?:boolean) {

      return (error: any): Observable<any> => {
         let errMsg: string;
         let status: number;
         status = error.status;
         const path = location.pathname;

         if (error instanceof HttpErrorResponse) {

            if(error.error.message){
               errMsg = error.error.message
            }else if(typeof error.error === 'string'){
               errMsg = error.error
            }else{
               errMsg = 'INTERNAL_ERROR'
            }

            if ((status === 401 && error.error.code) || (status === 401 && flagDownload) || (status === 500 && error.message === 'Stream closed')){
               localStorage.removeItem('access_token_dropbox');
               localStorage.removeItem('refresh_token_dropbox');
               return throwError(new Error(status, errMsg));
            }

            if (status === 401){
               localStorage.removeItem('access_token_dropbox');
               localStorage.removeItem('refresh_token_dropbox');
               this.translate.get('timeout').subscribe(s => {
                  this.toast.error(s);
               });
               const pathUrl = Path.combine('/login', path);
               this.router.navigate([pathUrl]);
            }

            return throwError(new Error(status, errMsg));
            } else {
               errMsg = error.message || error.toString();
               if (status === 401){
                  localStorage.removeItem('access_token_dropbox');
                  localStorage.removeItem('refresh_token_dropbox');
                  this.translate.get('timeout').subscribe(s => {
                     this.toast.error(s);
                  });
                  const pathUrl = Path.combine('/login', path);
                  this.router.navigate([pathUrl]);
               }
            }
         return throwError(new Error(error.status, errMsg));
      };
   }

   private handleErrorUpload() {
      return (error: any): Observable<any> => {
         let errMsg: string;
         if (error instanceof HttpErrorResponse) {
            errMsg = error.error;
            console.error(`${error.status} ${errMsg}`);
         } else {
            errMsg = error.message || error.toString();
         }
         return throwError(new Error(error.status, errMsg));
      };
   }

   toDataURL(url, callback) {
      const xhr = new XMLHttpRequest();
      xhr.onload = function() {
         const reader = new FileReader();
         reader.onloadend = function() {
            callback(reader.result);
         };
         reader.readAsDataURL(xhr.response);
      };
      xhr.open('GET', url);
      xhr.setRequestHeader('x-auth-token', this.user.token);
      xhr.responseType = 'blob';
      xhr.send();
   }

   public addShareId(objectId: any, objectType: string, UE: any, GP: any, ST: any) {
      const body = {objectId: objectId, objectType: objectType, usersAdd: UE, groupsAdd: GP, sitesAdd: ST};
      return this.post('/share/add', body);
   }

   public updateShareId(UE: any, GP: any, ST: any, SI: any) {
      const body = {shareId: SI, usersAdd: UE, groupsAdd: GP, sitesAdd: ST};
      return this.post('/share/update', body);
   }

   public getAllShare(SI: any) {
      return this.get('getAllShare/' + SI);
   }

   public unshare(objId: string, objType: string) {
      return this.get('share/unshare/' + objId + '/' + objType);
   }

   searchUserShare(kw?: any, contactId?: any) {
      return this.get('/search/user?kw=' + kw + '&contact=' + contactId);
   }
   searchGroupShare(kw?: any, groupId?: any) {
      return this.get('/search/group?kw=' + kw + '&groupId=' + groupId);
   }
   searchSiteShare(kw?: any) {
      return this.get('/search/site?kw=' + kw);
   }

   updatePermissionPrint(siteCode: any, memberName: any, memberType: any, isPrint: any) {
      const body = {siteCode: siteCode, name: memberName, type: memberType, isPrint: isPrint};
      return this.post('/permission/print/update', body);
   }
   config() {
      return this.get('/config');
   }
   ial(user: string) {
      return this.get('/ndid/userinfo/ial?userName='+user , {responseType: 'text'});
   }
   getCertUserlist(user: string, tin: string){
      const body = {user: user, tin: tin};
      return this.get('/list/usercert')
   }
   duplicateDoc(idFile: any) {
      return this.post('duplicate',idFile);
   }
   getAttachImage(taskId: number) {
      return this.get('/task/attach-image/' + taskId);
   }
   getShareLinkDropbox(token:string , path:string){
      const body = {token: token, refreshToken: localStorage.getItem('refresh_token_dropbox'), path:path};
      return this.post('dropbox/get/share-link',body)
         .pipe(catchError(this.handleErrorDropBox()));
   }
   getListFileDropbox(token:string, path:string, size?:number){
      if (!size) {
         size = 0;
      }
      const body = {token: token, refreshToken: localStorage.getItem('refresh_token_dropbox'), size: size};
      return this.post('dropbox/list?path=' + path,body)
         .pipe(catchError(this.handleErrorDropBox()));
   }
   getListFileDropboxWithCursor(token:string, path:string, size:number, hasMore: boolean, cursor: string){
      const body = {
         token: token,
         refreshToken: localStorage.getItem('refresh_token_dropbox'),
         size: size,
         hasMore: hasMore,
         cursor: cursor
      };
      return this.post('dropbox/list/cursor?path=' + path,body)
         .pipe(catchError(this.handleErrorDropBox()));
   }
   getSearchDropbox(token:string, query:string , pos:number){
      const body = {token: token, query: query, size:pos , refreshToken: localStorage.getItem('refresh_token_dropbox')};
      return this.post('dropbox/filesearch',body)
         .pipe(catchError(this.handleErrorDropBox()));
   }
   dropboxDownload(token:string, id:string , path:string , name:string){
      const body = {token: token, refreshToken: localStorage.getItem('refresh_token_dropbox'), id: id, path:path , name:name};
      return this.post('dropbox/download-by-filename',body, {responseType: 'blob'})
         .pipe(catchError(this.handleErrorDropBox()));
   }
   dropboxLink(token:string , path:string){
      const body = {token: token, refreshToken: localStorage.getItem('refresh_token_dropbox'), path:path};
      return this.post('dropbox/link',body)
         .pipe(catchError(this.handleErrorDropBox()));
   }
   dropboxDelete(token:string , path:string){
      const body = {token: token, refreshToken: localStorage.getItem('refresh_token_dropbox'), path:path};
      return this.post('dropbox/delete',body)
         .pipe(catchError(this.handleErrorDropBox()));
   }
   dropboxCreateFolder(token:string , path:string){
      const body = {token: token, refreshToken: localStorage.getItem('refresh_token_dropbox'), path:path};
      return this.post('dropbox/create/folder',body)
         .pipe(catchError(this.handleErrorDropBox()));
   }
   dropboxCreateFile(token:string , path:string, name: string, type:string ){
      const body = {
         token: token,
         refreshToken: localStorage.getItem('refresh_token_dropbox'),
         path: path,
         name: name,
         type: type
      };
      return this.post('dropbox/create/file',body)
         .pipe(catchError(this.handleErrorDropBox()));
   }

   dropBoxExportDocument(taskId:string,access_token: string, pathId?: string){
      const body = { taskId:taskId ,token: access_token, pathId: pathId };
      const api = '/task/export/dropbox'
      return this.http.post(this.url + api, body, this.createHttpOptions({responseType: 'text'}))
         .pipe(catchError(this.handleErrorDropBox()));
   }

   dropBoxAuthToken(accessCode:string , url:string){
      const body = {accessCode: accessCode, url : url};
      return this.post('dropbox/token' , body)
         .pipe(catchError(this.handleErrorDropBox()));
   }

   dropBoxUpdateFile(body: any){
      return this.put('dropbox/update',body)
         .pipe(catchError(this.handleErrorDropBox()));
   }


   dropboxUpload(formData: FormData){
      return this.post('dropbox/upload' , formData);
   }

   dropboxInitChunkedUpload(formData: FormData){
      return this.post('dropbox/upload/chunked/init' , formData ,{responseType: 'json'});
   }

   dropboxChunkedUpload(formData: FormData){
      return this.post('dropbox/upload/chunked/append' , formData ,{responseType: 'json'});
   }

   dropboxFinishChunkedUpload(formData: FormData){
      return this.post('dropbox/upload/chunked/finish' , formData, {responseType: 'json'});
   }

   getHistoryLogin() {
      return this.get('/history-login');
   }

   getHistoryLastlogin() {
      return this.get('/history-login-week');
   }

   updateStatusFactor(isTwoFactorLogin: boolean, isTwoFactorLoginGoogle: boolean,
      isTwoFactorSign: boolean, isTwoFactorSignGoogle: boolean, verificationGoogle: boolean){
         return this.post('/profile/updateStatusFactor?device=Web',
         {
            'enableTwoFactorLogin': isTwoFactorLogin,
            'enableTwoFactorLoginGoogle': isTwoFactorLoginGoogle,
            'enableTwoFactorSign': isTwoFactorSign,
            'enableTwoFactorSignGoogle': isTwoFactorSignGoogle,
            'verificationGoogle': verificationGoogle
         },
      {responseType: 'text'});
   }

   updateNotifyApp(isNotifyTasks: boolean, isNotifyTaskCompleted: boolean, isNotifyAppReceiveTask: boolean, isNotifyAppRejectTask: boolean,
      isNotifyAppReassignTask: boolean, isNotifyAppRemindTask: boolean, isNotifyAppTaskDelete: boolean, isNotifyAppTaskComplete: boolean,
      isNotifyAppUpdateDoc: boolean, isNotifyAppRevertDoc: boolean, isNotifyAppUpdateMet: boolean, isNotifyAppRenameDoc: boolean,
      isNotifyAppMoveDoc: boolean, isNotifyAppDocShared: boolean, isNotifyAppDocDelete: boolean) {
      return this.post('/profile/updateNotifyApp?device=Web',
         {
            'enableNotifyTasks': isNotifyTasks,
            'enableNotifyTaskCompleted': isNotifyTaskCompleted,
            'enableNotifyAppReceiveTask': isNotifyAppReceiveTask,
            'enableNotifyAppRejectTask': isNotifyAppRejectTask,
            'enableNotifyAppReassignTask': isNotifyAppReassignTask,
            'enableNotifyAppRemindTask': isNotifyAppRemindTask,
            'enableNotifyAppTaskDelete': isNotifyAppTaskDelete,
            'enableNotifyAppTaskComplete': isNotifyAppTaskComplete,
            'enableNotifyAppUpdateDoc': isNotifyAppUpdateDoc,
            'enableNotifyAppRevertDoc': isNotifyAppRevertDoc,
            'enableNotifyAppUpdateMet': isNotifyAppUpdateMet,
            'enableNotifyAppRenameDoc': isNotifyAppRenameDoc,
            'enableNotifyAppMoveDoc': isNotifyAppMoveDoc,
            'enableNotifyAppDocShared': isNotifyAppDocShared,
            'enableNotifyAppDocDelete': isNotifyAppDocDelete
         },
      {responseType: 'text'});
   }

   checkShare(shareMail: any , siteId : any){
      return this.post('/share/checkMailSite/' + siteId,shareMail);
   }

   getBrowserInfo() {
      const deviceInfo = this.deviceService.getDeviceInfo();
      const ua = navigator.userAgent;

      let osVersion = 'unknown';

      // iPhone
      const iPhoneMatch = ua.match(/iPhone OS\s([0-9_]+)/i);
      if (iPhoneMatch) {
         osVersion = `Mobile ${iPhoneMatch[1].replace(/_/g, '.')}`;
      }
      // iPad
      const iPadMatch = ua.match(/iPad.*OS\s([0-9_]+)/i);
      if (iPadMatch) {
         osVersion = `Mobile ${iPadMatch[1].replace(/_/g, '.')}`;
      }
      // Android and Mode Ipad
      const androidMatch = ua.match(/Android\s([0-9\.]+)/i);
      if (androidMatch) {
        osVersion = `Mobile ${androidMatch[1]}`;
      }
      // macOS
      const macMatch = ua.match(/Mac OS X\s([0-9_]+)/i);
      const isIPad = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
      // maxTouchPoints ของแต่ละตัวที่เปิดใช้งาน
      // Mac + Chrome	1
      // Mac + Safari	0
      // Mac + Edge		1
      // iPad Safari		5 / 10
      // iPad Chrome		5 / 10
      if (macMatch) {
        osVersion = `macOS ${macMatch[1].replace(/_/g, '.')}`;
      } else if (isIPad) { // ipad safari
         osVersion = 'Mobile'; // safari ของ ipad ไม่สามารถดึง version ได้
      }
      // Windows
      const winMatch = ua.match(/Windows NT\s([0-9\.]+)/i);
      if (winMatch) {
        osVersion = `Windows ${winMatch[1]}`;
      }

      const browserVersion = deviceInfo.browser_version
        ? deviceInfo.browser_version.split('.')[0]
        : '';

      return `${deviceInfo.browser} ${browserVersion} on ${osVersion}`;
    }

   getBusinessSiteBySiteCode(siteCode: string){
      return this.get('/eform/business-site/' + siteCode);
   }

   getEFormByXml(type: string, data: any){
      return this.post('eform/xml/convert/' + type,data, {responseType: 'json'})
         .pipe(catchError(this.handleErrorDropBox()));
   }

   convertPdfTemplate(docId: string) {
      return this.post('/pdf-template/flatten/' + docId);
   }
   loadCertification(docId: string) {
      return this.get('/loadCertification/' + docId);
   }

   // ขนาดไฟล์สูงสุดที่อัปโหลดได้ (bytes) default 50MB ใช้ค่านี้ถ้ายังโหลดจาก backend ไม่ได้
   fileMaxUpload = 50000000;
   private fileMaxUploadLoaded = false;

   loadFileMaxUpload() {
      if (this.fileMaxUploadLoaded) {
         return;
      }
      this.fileMaxUploadLoaded = true;
      this.get('task/config/max-upload').subscribe(r => {
         if (r?.fileMaxUpload > 0) {
            this.fileMaxUpload = r.fileMaxUpload;
         }
      }, () => this.fileMaxUploadLoaded = false);
   }

   // แปลง fileMaxUpload (bytes) เป็น MB สำหรับแสดงในข้อความแจ้งเตือน
   getFileMaxUploadMB(): number {
      return Math.round(this.fileMaxUpload / 1000000);
   }
}
